import * as React from "react";

/**
 * Shared 60-second ticker for relative timestamps. One interval drives every
 * subscribed cell, so a grid of "3 minutes ago" cells re-renders in a single
 * synchronized frame rather than via one timer per row. The interval is created
 * lazily on the first subscriber and cleared when the last one leaves, so it
 * idles whenever nothing on screen is recent enough to tick.
 */
const TICK_MS = 60_000;

const subscribers = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(onTick: () => void): () => void {
  subscribers.add(onTick);
  if (timer === null) {
    timer = setInterval(() => {
      for (const cb of subscribers) {
        cb();
      }
    }, TICK_MS);
  }
  return () => {
    subscribers.delete(onTick);
    if (subscribers.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/**
 * Re-render the calling component every 60s while `active`. Relative cells pass
 * `active` only while under the 24h threshold; static cells never subscribe, so
 * they cost nothing. AG Grid recycles cell renderers on scroll/sort — the
 * effect cleanup unsubscribes, so no stale callbacks and the timer stops once
 * the last recent cell unmounts.
 */
export function useRelativeTick(active: boolean): void {
  const [, forceRender] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!active) {
      return;
    }
    return subscribe(forceRender);
  }, [active]);
}
